/**
 * @file ShopFontsBlock.tsx
 * @description Блок «ВАШИ ШРИФТЫ · N из 5» в окне каталога (phase19.16 Р1-7;
 * SB99 — свои шрифты первыми с пометкой «ваш»; проект uiux §1): строка-кнопка
 * «＋ Загрузить свой шрифт» (56, мягкая — главное действие блока), строки
 * своих шрифтов. Виден при любом фильтре вида; поиск по названию отбирает и
 * свои.
 *
 * Состояния: список загружается — заготовка; не загрузился — полоса с
 * причиной и [Повторить], загрузка нового шрифта недоступна; своих нет —
 * подпись «Фирменный шрифт вашего бренда — до 5 шрифтов»; лимит — кнопка
 * выключена с объяснением. Без права настроек магазина — кнопок нет, вместо
 * них подпись, кто загружает шрифты.
 *
 * Строки — список (`role="list"`): в строке радиокнопка выбора и меню «⋯»,
 * поэтому `radiogroup` здесь не подходит.
 */

import { shopFontKey } from "@chaiberi/shared";
import { Plus } from "lucide-react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionLabel } from "../../ui/SectionLabel";
import { Skeleton } from "../../ui/skeleton";
import { useShopFonts } from "../shop-fonts-context";
import { ShopFontRow } from "./ShopFontRow";
import type { ShopFontDto } from "../../../lib/site-view/shop-fonts";

export const NO_FONT_RIGHTS_TEXT =
  "Свои шрифты загружает владелец магазина или сотрудник с правом настроек магазина.";

/** Атрибут кнопки загрузки — на неё возвращается фокус со второго экрана. */
export const UPLOAD_FONT_BUTTON_SELECTOR = "[data-upload-font]";

interface Props {
  /** Шрифты после поиска. */
  shown: ShopFontDto[];
  sample: string;
  chosen: string | null;
  current: string | null;
  onUpload: () => void;
  onSelect: (key: string) => void;
  onAddFile: (font: ShopFontDto) => void;
  onRename: (font: ShopFontDto) => void;
  onDelete: (font: ShopFontDto) => void;
}

export function ShopFontsBlock({ shown, sample, chosen, current, onUpload, onSelect, onAddFile, onRename, onDelete }: Props) {
  const shop = useShopFonts();
  const total = shop.items.length;
  const { limit } = shop;
  const pending = shop.canManage && !shop.loaded && !shop.error;
  const failed = shop.canManage && !shop.loaded && shop.error !== null;
  const full = total >= limit;

  return (
    <section aria-label="Ваши шрифты" className="flex flex-col">
      <SectionLabel as="h2" className="px-4 pb-2 pt-3">
        {pending || failed ? "Ваши шрифты" : `Ваши шрифты · ${total} из ${limit}`}
      </SectionLabel>
      {!shop.canManage ? (
        <p className="type-caption px-4 pb-3 text-on-surface-variant">{NO_FONT_RIGHTS_TEXT}</p>
      ) : (
        <div className="flex flex-col gap-2 px-4 pb-3">
          {failed ? (
            <NoticeStrip
              tone="error"
              action={
                <Button variant="outline" onClick={() => void shop.reload()}>
                  Повторить
                </Button>
              }
            >
              {shop.error}
            </NoticeStrip>
          ) : null}
          {pending ? (
            <Skeleton className="h-14 w-full rounded-lg" aria-label="Загружаем ваши шрифты" />
          ) : (
            // h-14, а не min-h: тема задаёт кнопкам min-height (44 на ПК) поверх утилит.
            <Button variant="soft" className="h-14 w-full justify-start" disabled={full || failed} onClick={onUpload} data-upload-font="">
              <Plus aria-hidden />
              Загрузить свой шрифт
            </Button>
          )}
          {shop.loaded && full ? (
            <p className="type-caption text-on-surface-variant">
              Загружено {limit} из {limit} шрифтов. Чтобы добавить новый, удалите тот, что не используется.
            </p>
          ) : shop.loaded && total === 0 ? (
            <p className="type-caption text-on-surface-variant">Фирменный шрифт вашего бренда — до {limit} шрифтов</p>
          ) : null}
        </div>
      )}
      {shown.length > 0 ? (
        <div role="list" aria-label="Ваши шрифты">
          {shown.map((font) => {
            const key = shopFontKey(font.id);
            return (
              <ShopFontRow
                key={font.id}
                font={font}
                sample={sample}
                selected={key === chosen}
                current={key === current}
                canManage={shop.canManage}
                onSelect={() => onSelect(key)}
                onAddFile={() => onAddFile(font)}
                onRename={() => onRename(font)}
                onDelete={() => onDelete(font)}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
